import { isEmpty } from "lodash-es";
import React, { useEffect, useState } from "react";

import { useProductServiceApi } from "@api/hooks/productService.hook";
import {
    ProductTileMapper,
    ProductTileModel,
    ProductsMapper,
} from "@api/mappers/products.mapper";

import {
    PageTemplates,
    isPageTemplate,
} from "@utils/configuration/pageTemplate.helpers";
import { ProductFieldSet, ProductSliderSources } from "@utils/enums";
import { getBreakPointLayoutSettings } from "@utils/swiper";

import {
    getRecentlyViewed,
    removeItemFromRecentlyViewed,
    removeRecentlyViewed,
} from "@services/recentlyViewedProducts.service";

import { ProductSlider } from "@components/Product/ProductSlider/productSlider.component";
import { ProductSliderSkeletonComponent } from "@components/Product/ProductSlider/skeleton/productSliderSkeleton.component";

const productsMapper = ProductsMapper(ProductTileMapper);

type ProductSliderProps = {
    title: string;
    showButton: boolean;
    showCategoryLink: boolean;
    useInsidePadding: boolean;
    amountOfFeatures: number;
    breakpointslayoutsettings: any;
    ssrLayoutClasses?: string;
    lazyLoad: boolean;
    productSku?: string;
};

type Props = ProductSliderProps & {};

const RecentsProductslider = ({
    title,
    showButton,
    amountOfFeatures,
    showCategoryLink,
    breakpointslayoutsettings,
    ssrLayoutClasses,
    lazyLoad,
    productSku,
}: Props) => {
    const [products, setProducts] = useState<Array<ProductTileModel>>([]);

    const [{ loading }, loadProducts] = useProductServiceApi(
        {
            url: `/products`,
            method: "GET",
            mapper: productsMapper,
        },
        {
            manual: true,
        },
    );

    useEffect(() => {
        const recentlyViewedSkus = getRecentlyViewed(
            isPageTemplate(PageTemplates.PDP) ? productSku : undefined,
        );

        if (isEmpty(recentlyViewedSkus)) return;

        loadProducts({
            params: {
                sku: recentlyViewedSkus.join(","),
                fields: ProductFieldSet.ProductTile,
            },
        }).then(({ data }) => {
            setProducts(data);
        });
    }, [productSku, loadProducts, setProducts]);

    const deleteFromHistory = (sku: string) => {
        removeItemFromRecentlyViewed(sku);
        setProducts(prevSkus => prevSkus.filter(item => item.sku !== sku));
    };

    const deleteRecentsHistory = () => {
        removeRecentlyViewed();
        setProducts([]);
    };

    if (isEmpty(products) && !loading) {
        return null;
    }

    if (loading) {
        return <ProductSliderSkeletonComponent isRecent={true} />;
    }

    return (
        <ProductSlider
            lazyLoad={lazyLoad}
            title={title}
            products={products}
            useInsidePadding={true}
            productsource={ProductSliderSources.recent}
            deleteFromHistory={deleteFromHistory}
            deleteRecentsHistory={deleteRecentsHistory}
            showButton={showButton}
            amountOfFeatures={amountOfFeatures}
            showCategoryLink={showCategoryLink}
            breakpointslayoutsettings={getBreakPointLayoutSettings(
                breakpointslayoutsettings,
            )}
            ssrLayoutClasses={ssrLayoutClasses}
            analyticsContext={{
                list: "product-slider-recents",
                listTitle: title,
            }}
        />
    );
};

export { RecentsProductslider };
